import React from "react";
import {
  View,
  ScrollView,
  TouchableOpacity,
  Text
} from "react-native";
import { ODSIcon } from "../../../openbrix-design-system/ui/atoms/ODSIcon";
import ODSButton from "../../../openbrix-design-system/ui/atoms/ODSButton";
import { formatDate } from "../../helpers/functionHelpers";
import { navigateAction } from "../../helpers/navigationHelpers";
import MaintenanceCard from "../../../openbrix-design-system/ui/molecules/Report/MaintenanceCard";

// ✅ Types
export interface IMaintenanceItem {
  open_flow_maintenance_issue_id: number | string;
  title: string;
  status: string;
  category_id: number | string;
  room_type_id: number | string;
  date_opened: string;
  description: string;
  images: string[];
  videos: string[];
}

export interface ICategoryType {
  open_flow_maintenance_issue_category_id: number | string;
  name: string;
}

export interface IRoomType {
  open_flow_maintenance_issue_room_type_id: number | string;
  name: string;
}

interface IProps {
  data: IMaintenanceItem[];
  expandedId: number | string | null;
  onItemPress: (id: number | string) => void;
  roomTypes: IRoomType[];
  categoryTypes: ICategoryType[];
  flowId: string;
  navigation: any;
}

// ✅ Status badge with better styling
const StatusBadge = ({ status }: { status: string }) => {
  const config = {
    Completed: { color: "#059669", bg: "#D1FAE5" },
    pending: { color: "#D97706", bg: "#FEF3C7" },
    PENDING: { color: "#DC2626", bg: "#FEE2E2" }
  }[status] || { color: "#6B7280", bg: "#F3F4F6" };

  return (
    <View style={{
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 12,
      backgroundColor: config.bg,
      alignSelf: 'flex-start',
    }}>
      <Text style={{ 
        color: config.color, 
        fontSize: 11, 
        fontWeight: "600",
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}>
        {status}
      </Text>
    </View>
  );
};

// ✅ Label-Value component for better organization
const LabelValue = ({ label, value, icon }: { label: string, value: string, icon?: string }) => (
  <View style={{ marginBottom: 8 }}>
    <Text style={{ 
      fontSize: 11, 
      color: "#64748B", 
      fontWeight: "500",
      marginBottom: 2,
    }}>
      {label}
    </Text>
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      {icon && <ODSIcon icon={icon} size={12} style={{ marginRight: 4 }} />}
      <Text style={{ 
        fontSize: 13, 
        color: "#1E293B", 
        fontWeight: "400",
      }}>
        {value}
      </Text>
    </View>
  </View>
);



// ✅ Header Component
const ListHeader = () => (
  <View style={{
    padding: 16,
    paddingBottom: 8,
  }}>
    <Text style={{
      fontSize: 24,
      fontWeight: "700",
      color: "#1E293B",
      marginBottom: 4,
    }}>
      Maintenance Issues
    </Text>
    <Text style={{
      fontSize: 14,
      color: "#64748B",
      marginBottom: 16,
    }}>
      View and manage your maintenance requests
    </Text>
  </View>
);

// ✅ MOBILE MAIN COMPONENT
const MobileMaintenanceView = (props: IProps) => {
  const { data, expandedId, onItemPress, roomTypes, categoryTypes, flowId, navigation } = props;

  return (
    <View style={{
      flex: 1,
      backgroundColor: "#F8FAFC",
    }}>
      <ListHeader />
      
      <ScrollView 
        showsVerticalScrollIndicator={false}
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: 12, paddingBottom: 20 }}
      >
        {data.length === 0 ? (
          <View style={{
            alignItems: "center",
            justifyContent: "center",
            paddingVertical: 60,
            paddingHorizontal: 20,
            backgroundColor: '#FFFFFF',
            borderRadius: 12,
            margin: 12,
            borderWidth: 1,
            borderColor: '#E2E8F0',
          }}>
            <ODSIcon icon="Document" size={48} color="#94A3B8" />
            <Text style={{ 
              marginTop: 16, 
              color: "#64748B",
              fontSize: 16,
              textAlign: "center",
              fontWeight: '500',
            }}>
              No maintenance issues found
            </Text>
            <Text style={{ 
              marginTop: 8, 
              color: "#94A3B8",
              fontSize: 14,
              textAlign: "center",
            }}>
              Create your first maintenance issue to get started
            </Text>
          </View>
        ) : (
          data.map((item) => (
            <MaintenanceCard
              key={String(item.open_flow_maintenance_issue_id)}
              item={item}
              isExpanded={expandedId === item.open_flow_maintenance_issue_id}
              onPress={() => onItemPress(item.open_flow_maintenance_issue_id)}
              onEdit={() => navigateAction(navigation, "Maintenance", { 
                id: flowId, 
                maintainance_id: item.open_flow_maintenance_issue_id 
              })}
              roomTypes={roomTypes}
              categoryTypes={categoryTypes}
            />
          ))
        )}
      </ScrollView>
    </View>
  );
};

export default MobileMaintenanceView;